import {Injectable} from '@angular/core';

import {from, Observable} from 'rxjs';
import {filter, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from './../../destroyable.component';

import {KeycloakEventType, KeycloakService} from 'keycloak-angular';
import {KeycloakEvent} from 'keycloak-angular/lib/core/interfaces/keycloak-event';
import {KeycloakLoginOptions} from 'keycloak-js';
import {
  LOCAL_STORAGE_ID_TOKEN_KEY_NAME,
  LOCAL_STORAGE_REFRESH_TOKEN_KEY_NAME,
  LOCAL_STORAGE_TOKEN_KEY_NAME
} from '../../app-initializer';
import {RESIZE_EVENT} from '../../app.constants';
import {ModalService} from '../modal/modal.service';

function removeLocalStorageToken(name: string) {
    localStorage.removeItem(name);
}

export const clearLocalStorageToken = () => {
  removeLocalStorageToken(LOCAL_STORAGE_TOKEN_KEY_NAME);
  removeLocalStorageToken(LOCAL_STORAGE_REFRESH_TOKEN_KEY_NAME);
  removeLocalStorageToken(LOCAL_STORAGE_ID_TOKEN_KEY_NAME);
};

/**
 * Wrapper for mocking purpose
 */
@Injectable()
export class KeycloakWrapperService extends Destroyable{
  public get tokenParsed(): any {
    return this._tokenParsed;
  }

  public get isAccessLimited(): boolean {
    return this.tokenParsed?.gp?.accesslimited === 'true';
  }

  private modal: Promise<any>;
  private _tokenParsed: any;

  constructor(private keycloakService: KeycloakService,
              private modalService: ModalService) {

    super();

    clearLocalStorageToken();

    keycloakService.keycloakEvents$.pipe(
      tap((event: KeycloakEvent) => {
        if (event.type === KeycloakEventType.OnReady) {
          return;
        }
      }),
      filter(event => event.type === KeycloakEventType.OnTokenExpired),
      switchMap(() => this.getToken(5)),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public getToken(minValidity: number = 5): Observable<string> {
    return from(new Promise<string>((resolv) => {
      if (this.keycloakService?.getKeycloakInstance()) {
        if (this.keycloakService.isTokenExpired(minValidity)) {
          this._tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
          this.keycloakService.updateToken(minValidity)
            .then(() => {
              resolv(<string>this.keycloakService.getKeycloakInstance().token);
            })
            .catch(() => {
              console.warn('Failed to refresh token');
              this.expiredDialog().then(() => {
                this.logout();
                resolv(null);
              });
            });
        } else {
          this._tokenParsed = this.keycloakService.getKeycloakInstance()?.tokenParsed;
          resolv(<string>this.keycloakService.getKeycloakInstance()?.token);
        }
      }
    }));
  }

  private expiredDialog(): Promise<any> {
    if (!this.modal) {
      this.modal = this.modalService.alert({title: 'DIALOG.auth_failed', text: 'DIALOG.expired_token'});
      window.dispatchEvent(RESIZE_EVENT);
    }

    return this.modal;
  }

  public logout() {
    if (this.keycloakService.isLoggedIn()) {
      this.keycloakService.logout();
    }
  }

  public login(options: KeycloakLoginOptions) {
    return this.keycloakService.login(options);
  }

  public hasResourceRole(role: string, api: string) {
    return this.keycloakService.getKeycloakInstance().hasResourceRole(role, api);
  }

  public hasRealmRole(role: string) {
    return this.keycloakService.getKeycloakInstance().hasRealmRole(role);
  }
}
